import React from 'react';
import { Trans } from '@lingui/macro';

import Calendar from '@rover/icons/svg/calendar.svg';
import CreditCard from '@rover/icons/svg/credit-card.svg';
import HelpBubble from '@rover/icons/svg/text-help.svg';
import { Box, Display, Flex, Heading, Link, Paragraph } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box';
import { Props as DisplayProps } from '@rover/kibble/core/Display';
import { Props as FlexProps } from '@rover/kibble/core/Flex';
import type { Props as HeadingProps } from '@rover/kibble/core/Heading';
import { Props as ParagraphProps } from '@rover/kibble/core/Paragraph';
import Section from '@rover/kibble/patterns/Section';

export const IconOptions = {
  CALENDAR: <Calendar />,
  CREDIT_CARD: <CreditCard />,
  HELP_BUBBLE: <HelpBubble />,
};

export type WhyChooseRoverWrapperProps = Omit<BoxProps, 'variant'> & {
  htmlRef?: React.RefObject<HTMLElement>;
};

const WhyChooseRoverWrapper = ({
  htmlRef,
  children,
  ...props
}: WhyChooseRoverWrapperProps): JSX.Element => {
  return (
    <Section pt={['8x', '12x']} pb={['0x', '8x']} htmlRef={htmlRef} {...props}>
      {children}
    </Section>
  );
};

WhyChooseRoverWrapper.Display = (props: DisplayProps): JSX.Element => (
  <Display
    size="500"
    webProps={{ as: 'h3', responsive: false }}
    textAlign="center"
    mb="8x"
    {...props}
  />
);

WhyChooseRoverWrapper.List = (props: FlexProps): JSX.Element => {
  return (
    <Flex
      webAs="ul"
      width="100%"
      pl="0x"
      flexDirection={['column', 'row']}
      alignItems={['center', 'flex-start']}
      justifyContent={['flex-start', 'space-between']}
      {...props}
    >
      {props.children}
    </Flex>
  );
};

WhyChooseRoverWrapper.ListItem = (props: BoxProps & { icon: React.ReactNode }): JSX.Element => {
  return (
    <Box
      webAs="li"
      mx="3x"
      mb={['8x', '0x']}
      textAlign="center"
      width="100%"
      maxWidth="300px"
      sx={{ listStyleType: 'none' }}
      {...props}
    >
      <Box mb="3x">{props.icon}</Box>
      {props.children}
    </Box>
  );
};

WhyChooseRoverWrapper.ItemHeading = (props: HeadingProps): JSX.Element => (
  <Heading size="300" mb="3x">
    {props.children}
  </Heading>
);

WhyChooseRoverWrapper.ItemContent = (props: ParagraphProps): JSX.Element => {
  return <Paragraph {...props} />;
};

export type Props = {
  reservationProtection: string;
  supportText: string;
  supportUrl: string;
  htmlRef?: React.RefObject<HTMLElement>;
};

const WhyChooseRover: React.FC<Props> = ({
  reservationProtection,
  supportText,
  supportUrl,
  htmlRef,
}: Props) => {
  return (
    <WhyChooseRoverWrapper htmlRef={htmlRef}>
      <WhyChooseRoverWrapper.Display>
        <Trans>Why Choose Rover?</Trans>
      </WhyChooseRoverWrapper.Display>
      <WhyChooseRoverWrapper.List>
        <WhyChooseRoverWrapper.ListItem icon={IconOptions.CREDIT_CARD}>
          <WhyChooseRoverWrapper.ItemHeading>
            <Trans>Cashless payments</Trans>
          </WhyChooseRoverWrapper.ItemHeading>
          <WhyChooseRoverWrapper.ItemContent>
            <Trans>Book and pay through Rover’s secured platform—no cash or checks required.</Trans>
          </WhyChooseRoverWrapper.ItemContent>
        </WhyChooseRoverWrapper.ListItem>
        <WhyChooseRoverWrapper.ListItem icon={IconOptions.CALENDAR}>
          <WhyChooseRoverWrapper.ItemHeading>
            <Trans>Reservation protection</Trans>
          </WhyChooseRoverWrapper.ItemHeading>
          <WhyChooseRoverWrapper.ItemContent>
            {reservationProtection}
          </WhyChooseRoverWrapper.ItemContent>
        </WhyChooseRoverWrapper.ListItem>
        <WhyChooseRoverWrapper.ListItem icon={IconOptions.HELP_BUBBLE}>
          <WhyChooseRoverWrapper.ItemHeading>
            <Trans>World-class support</Trans>
          </WhyChooseRoverWrapper.ItemHeading>
          <WhyChooseRoverWrapper.ItemContent>
            <Trans>
              {supportText} You can <Link href={supportUrl}>get in touch</Link> with us anytime.
            </Trans>
          </WhyChooseRoverWrapper.ItemContent>
        </WhyChooseRoverWrapper.ListItem>
      </WhyChooseRoverWrapper.List>
    </WhyChooseRoverWrapper>
  );
};

export default WhyChooseRover;
